import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';

import Landing from './pages/Landing';
import Login from './pages/Login';
import CharacterSelect from './pages/CharacterSelect';
import Dashboard from './pages/Dashboard';
import Transfers from './pages/Transfers';
import History from './pages/History';
import Company from './pages/Company';
import Admin from './pages/Admin';
import Invoices from './pages/Invoices';
import ATM from './pages/ATM';

import './index.css';

// ==================== AUTH CONTEXT ====================
export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('fleeca_token'));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!!localStorage.getItem('fleeca_token'));

  const fetchUser = useCallback(async (t) => {
    if (!t) { setUser(null); setLoading(false); return; }
    try {
      const res = await axios.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${t}` },
      });
      setUser(res.data);
    } catch (_) {
      localStorage.removeItem('fleeca_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser(token);
  }, [token, fetchUser]);

  // Intercepteur global : token expiré → déconnexion
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      res => res,
      err => {
        if (err.response?.status === 401) {
          localStorage.removeItem('fleeca_token');
          setToken(null);
          setUser(null);
          window.location.href = '/';
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const login = (newToken) => {
    localStorage.setItem('fleeca_token', newToken);
    setToken(newToken);
  };

  const logout = () => {
    localStorage.removeItem('fleeca_token');
    setToken(null);
    setUser(null);
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ token, user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// ==================== ROUTE PROTÉGÉE ====================
function PrivateRoute({ children }) {
  const { token, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!token) return <Navigate to="/" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { token, user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!token) return <Navigate to="/" replace />;       // pas connecté → landing
  if (!user) return <LoadingScreen />;                   // token ok, user en cours
  if (!user.isAdmin) return <Navigate to="/dashboard" replace />; // connecté non-admin
  return children;
}

function LoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', background: 'var(--surface)',
    }}>
      <i className="fa-solid fa-building-columns spinner" style={{ fontSize: 40, color: 'var(--primary)', marginBottom: 16 }} />
      <p style={{ color: 'var(--on-surface-variant)', fontFamily: 'Manrope', fontWeight: 600 }}>Chargement...</p>
    </div>
  );
}

// ==================== APP ====================
export default function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/select" element={<CharacterSelect />} />
          <Route path="/dashboard" element={
            <PrivateRoute><Dashboard /></PrivateRoute>
          } />
          <Route path="/transfers" element={
            <PrivateRoute><Transfers /></PrivateRoute>
          } />
          <Route path="/history" element={
            <PrivateRoute><History /></PrivateRoute>
          } />
          <Route path="/company" element={
            <PrivateRoute><Company /></PrivateRoute>
          } />
          <Route path="/invoices" element={
            <PrivateRoute><Invoices /></PrivateRoute>
          } />
          <Route path="/admin" element={
            <AdminRoute><Admin /></AdminRoute>
          } />
          <Route path="/atm" element={<ATM />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}
